import { lazy } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { useLoggedInUserFromContext } from '../contexts/loggedInUserContext';
import { carePortalUrl } from '../hooks/useChangePage/useChangePage';

// Lazy loaded components
const ProvidersContainer = lazy(() => import('../contexts/ProvidersContainer/ProvidersContainer'));
const CarePortalLayoutComponent = lazy(() => import('../features/carePortalLayout/CarePortalLayoutComponent/CarePortalLayoutComponent'));
const ProtectedLayout = lazy(() => import('../features/layout/ProtectedLayout/ProtectedLayout'));
const HomePageComponent = lazy(() => import('../features/home/component/HomePageComponent/HomePageComponent'));
const CalendarPageComponent = lazy(() => import('../features/calendar/component/CalendarPageComponent/CalendarPageComponent'));
const EnrollmentPageComponent = lazy(() => import('../features/enrollment/component/EnrollmentPageComponent/EnrollmentPageComponent'));
const PatientTableDispatcherComponent = lazy(() => import('../features/patient/component/PatientTableDispatcherComponent/PatientTableDispatcherComponent'));
const PatientProfilePageComponent = lazy(() => import('../features/patient/page/PatientProfilePageComponent/PatientProfilePageComponent'));
const PatientCarePageComponent = lazy(() => import('../features/patientCare/component/PatientCarePageComponent/PatientCarePageComponent'));
const VisitsAndEventsComponent = lazy(() => import('../features/visit/component/VisitsAndEventsLayoutComponent/VisitsAndEventsLayoutComponent'));
const PlaygroundComponent = lazy(() => import('../features/playground/PlaygroundComponent'));
const PatientInfoQAComponent = lazy(() => import('../features/qa/component/PatientInfoQAComponent/PatientInfoQAComponent'));
const TaskDashboardComponent = lazy(() => import('../features/taskAssignment/component/TaskDashboardComponent/TaskDashboardComponent'));
const TicketDashboardContainer = lazy(() => import('../features/ticket/container/TicketDashboardContainer/TicketDashboardContainer'));
const MedicalAlertDashboardComponent = lazy(() => import('../features/medicalAlerts/component/MedicalAlertDashboardComponent/MedicalAlertDashboardComponent'));
const BillingReportPageComponent = lazy(() => import('../features/billingReport/component/BillingReportPageComponent/BillingReportPageComponent'));
const DataAnalyticsMainContainer = lazy(() => import('../features/dataAnalytics/DataAnalyticsMainContainer'));
const MessagePageComponent = lazy(() => import('../features/message/component/MessagePageComponent/MessagePageComponent'));
const UtilitiesPageComponent = lazy(() => import('../features/utilities/component/UtilitiesPageComponent/UtilitiesPageComponent'));
const TranscribePageComponent = lazy(() => import('../features/transcribing/page/TranscribePageComponent'));
const IcareConversionPageComponent = lazy(() => import('../features/iCareConversion/page/IcareConversionPageComponent'));
const BillableTimeReviewPageComponent = lazy(() => import('../features/billableTime/component/BillableTimeReviewPageComponent/BillableTimeReviewPageComponent'));
const BillableTimeDetailedLogPageComonent = lazy(() => import('../features/billableTime/component/BillableTimeDetailedLogPageComponent/BillableTimeDetailedLogPageComonent'));
const PriorAuthPageComponent = lazy(() => import('../features/Insurance/component/PriorAuthPageComponent/PriorAuthPageComponent'));
const CGMReportPageComponent = lazy(() => import('../features/CGMReport/page/CGMReportPageComponent/CGMReportPageComponent'));
const InsuranceAndEligibilityPageComponent = lazy(() => import('../features/Insurance/component/InsuranceAndEligibilityPageComponent/InsuranceAndEligibilityPageComponent'));
const DeviceLookupPageComponent = lazy(() => import('../features/device/component/DeviceLookupPageComponent/DeviceLookupPageComponent'));
const UserProfileComponent = lazy(() => import('../features/user/component/UserProfileComponent/UserProfileComponent'));
const ClinicStaffBillingPageComponent = lazy(() => import('../features/ClinicStaffBilling/component/ClinicStaffBillingPageComponent/ClinicStaffBillingPageComponent'));
const PaymentAndInvoicePageComponent = lazy(() => import('../features/ClinicStaffBilling/component/PaymentAndInvoicePageComponent/PaymentAndInvoicePageComponent'));
const ClinicOKRPageComponent = lazy(() => import('../features/adminPortal2/clinicOKR/component/ClinicOKRPageComponent/ClinicOKRPageComponent'));

export const createCarePortalRoutes = () => {
  const { isProvider, isClinicBiller, isClinicManager } = useLoggedInUserFromContext();

  const getPortalRoute = () => {
    if (isProvider) {
      return '/care-portal/patients';
    }
    if (isClinicBiller || isClinicManager) {
      return '/care-portal/clinic-billing';
    }
    return '/care-portal/home';
  };

  return (
    <Route path={carePortalUrl}>
      <Route
        element={<ProtectedLayout />}
      >
        <Route
          index
          element={<Navigate to={getPortalRoute()} />}
        />
        <Route element={<ProvidersContainer />}>
          <Route
            path="/care-portal/clinic-okr"
            element={<ClinicOKRPageComponent />}
          />
          <Route element={<CarePortalLayoutComponent />}>
            <Route path="/care-portal/home" element={<HomePageComponent />} />
            <Route path="/care-portal/calendar" element={<CalendarPageComponent />} />
            <Route path="/care-portal/enrollment" element={<EnrollmentPageComponent />} />
            <Route path="/care-portal/patients" element={<PatientTableDispatcherComponent />} />
            <Route path="/care-portal/patients/:patientId">
              <Route index element={<PatientProfilePageComponent />} />
            </Route>
            <Route path="/care-portal/patient-care" element={<PatientCarePageComponent />} />
            <Route path="/care-portal/patient-care/:patientId" element={<PatientCarePageComponent />} />
            <Route path="/care-portal/visits-and-events" element={<VisitsAndEventsComponent />} />
            <Route path="/care-portal/playground" element={<PlaygroundComponent />} />
            <Route path="/care-portal/qa/">
              <Route index element={<PatientInfoQAComponent />} />
            </Route>
            <Route path="/care-portal/qa/:patientId">
              <Route index element={<PatientInfoQAComponent />} />
            </Route>
            <Route path="/care-portal/task-list" element={<TaskDashboardComponent />} />
            <Route path="/care-portal/task" element={<TicketDashboardContainer />} />
            <Route path="/care-portal/medical-alert-dashboard" element={<MedicalAlertDashboardComponent />} />
            <Route path="/care-portal/clinic-billing/billing-report-page" element={<BillingReportPageComponent />} />
            <Route path="/care-portal/insights">
              <Route index element={<DataAnalyticsMainContainer />} />
            </Route>
            <Route path="/care-portal/messages" element={<MessagePageComponent />} />
            <Route path="/care-portal/utilities">
              <Route index element={<UtilitiesPageComponent />} />
              <Route path="/care-portal/utilities/transcribe" element={<TranscribePageComponent />} />
              <Route path="/care-portal/utilities/conversion-status" element={<IcareConversionPageComponent />} />
              <Route path="/care-portal/utilities/billable-time-review" element={<BillableTimeReviewPageComponent />} />
              <Route path="/care-portal/utilities/billable-time-detailed-log" element={<BillableTimeDetailedLogPageComonent />} />
              <Route path="/care-portal/utilities/medical-alert-dashboard" element={<MedicalAlertDashboardComponent />} />
              <Route path="/care-portal/utilities/prior-auth" element={<PriorAuthPageComponent />} />
              <Route path="/care-portal/utilities/cgm-reports" element={<CGMReportPageComponent />} />
              <Route path="/care-portal/utilities/insurance-and-eligibility" element={<InsuranceAndEligibilityPageComponent />} />
              <Route path="/care-portal/utilities/device-lookup" element={<DeviceLookupPageComponent />} />
            </Route>
            <Route path="/care-portal/settings" element={<UserProfileComponent />} />
            <Route path="/care-portal/clinic-billing" element={<ClinicStaffBillingPageComponent />} />
            <Route path="/care-portal/clinic-billing/payment-and-invoice" element={<PaymentAndInvoicePageComponent />} />
          </Route>
        </Route>
      </Route>
    </Route>
  );
};

export default createCarePortalRoutes;
